import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FlipHorizontal2, Wifi, WifiOff, CheckCircle2, Loader2, UploadCloud, AlertCircle, Play, ArrowLeft, X, Trash2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { savePendingRecording } from '../utils/offlineDB';
import { BarcodeScanner, EnumScanMode } from 'dynamsoft-barcode-reader-bundle';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECORD_SECONDS = 5;

const MobilePackingStation = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [facingMode, setFacingMode] = useState('environment');
  const [phase, setPhase] = useState('scanning');
  const [countdown, setCountdown] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [cameraError, setCameraError] = useState('');
  const [playingVideo, setPlayingVideo] = useState(null);
  const [loadingPlayId, setLoadingPlayId] = useState(null);

  const scannerContainerRef = useRef(null); // Dynamsoft mounts its video here
  const scannerRef = useRef(null); // BarcodeScanner instance
  const scannerStartingRef = useRef(false); // guard against double start
  const streamRef = useRef(null); // camera stream for MediaRecorder
  const chunksRef = useRef([]);
  const isRecordingRef = useRef(false);
  const lastScanTimeRef = useRef(0);
  const lastScanIdRef = useRef('');
  const audioCtxRef = useRef(null);

  const api = useMemo(() => axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  // ─── Feedback ────────────────────────────────────────────────────────────────

  const playBeep = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window['webkitAudioContext'];
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioCtx();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1200;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
      osc.onended = () => {
        osc.disconnect();
        gain.disconnect();
      };
    } catch (_) { }
  }, []);

  const vibrate = useCallback(() => {
    try { navigator.vibrate?.([80, 40, 80]); } catch (_) { }
  }, []);

  // ─── Upload ───────────────────────────────────────────────────────────────────

  const uploadVideo = useCallback(async (blob, trackingId, startTime) => {
    if (!navigator.onLine) {
      const buf = await blob.arrayBuffer();
      await savePendingRecording({
        trackingId,
        executiveId: user?.id,
        executiveName: user?.name,
        duration: RECORD_SECONDS,
        videoBlob: buf,
        startTime,
      });
      toast.warning(`Saved locally — will sync when online`);
      return false;
    }

    try {
      const { data: { sasUrl, blobName, cloudUrl } } = await api.get(
        `/recordings/upload-url?trackingId=${encodeURIComponent(trackingId)}`
      );

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', sasUrl);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', 'video/webm');
        xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Azure ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(blob);
      });

      const { data: { recording } } = await api.post('/recordings/confirm-upload', {
        trackingId, blobName, cloudUrl,
        duration: RECORD_SECONDS,
        startTime,
        fileSize: blob.size,
      });

      toast.success(`Uploaded: ${trackingId}`);
      return { uploaded: true, recordingId: recording?.id || recording?._id };
    } catch (err) {
      try {
        const buf = await blob.arrayBuffer();
        await savePendingRecording({
          trackingId,
          executiveId: user?.id,
          executiveName: user?.name,
          duration: RECORD_SECONDS,
          videoBlob: buf,
          startTime,
        });
        toast.warning(`Upload failed — saved locally`);
      } catch (_) {
        toast.error(`Failed to save recording for ${trackingId}`);
      }
      return false;
    }
  }, [api, user]);

  // ─── Recording ────────────────────────────────────────────────────────────────

  const startRecording = useCallback(async (trackingId) => {
    if (isRecordingRef.current || !streamRef.current) return;
    isRecordingRef.current = true;

    const startTime = new Date().toISOString();
    chunksRef.current = [];

    let mediaRecorder;
    try {
      mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp8',
        videoBitsPerSecond: 500000,
      });
    } catch {
      mediaRecorder = new MediaRecorder(streamRef.current);
    }

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    // single start reduces GC pressure vs 500ms chunks
    mediaRecorder.start();

    // pause scanning while the encoder works to reduce device load
    try { scannerRef.current?.pauseScanning?.(); } catch (_) { }

    for (let i = RECORD_SECONDS; i >= 1; i--) {
      setCountdown(i);
      setPhase('countdown');
      await new Promise(r => setTimeout(r, 1000));
    }
    setCountdown(null);
    setPhase('uploading');

    mediaRecorder.stop();
    await new Promise(r => { mediaRecorder.onstop = r; });

    const blob = new Blob(chunksRef.current, { type: 'video/webm' });

    const scanEntry = { id: Date.now(), trackingId, startTime, status: 'uploading', recordingId: null };
    setRecentScans(prev => [scanEntry, ...prev.slice(0, 49)]);

    const result = await uploadVideo(blob, trackingId, startTime);

    setRecentScans(prev =>
      prev.map(s => s.id === scanEntry.id
        ? { ...s, status: result ? 'uploaded' : 'local', recordingId: result?.recordingId || null }
        : s
      )
    );

    isRecordingRef.current = false;
    try { scannerRef.current?.resumeScanning?.(); } catch (_) { }
    setPhase('scanning');
  }, [uploadVideo]);

  // ─── Play / Delete ────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async (scan) => {
    if (!scan.recordingId) { toast.error('Recording ID not available'); return; }
    try {
      await api.delete(`/recordings/${scan.recordingId}`);
      setRecentScans(prev => prev.filter(s => s.id !== scan.id));
      toast.success(`Deleted: ${scan.trackingId}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  }, [api]);

  const handlePlay = useCallback(async (scan) => {
    if (!scan.recordingId) { toast.error('Recording ID not available'); return; }
    setLoadingPlayId(scan.id);
    try {
      const { data } = await api.get(`/recordings/${scan.recordingId}/play-url`);
      const playUrl = data.source === 'local' ? `${API_URL}${data.playUrl}` : data.playUrl;
      setPlayingVideo({ trackingId: scan.trackingId, playUrl });
    } catch (err) {
      toast.error('Could not load video');
    } finally {
      setLoadingPlayId(null);
    }
  }, [api]);

  // ─── Barcode Scanner — Dynamsoft BarcodeScanner ───────────────────────────────

  const stopScanner = useCallback(async () => {
    scannerStartingRef.current = false;
    if (scannerRef.current && !scannerRef.current.disposed) {
      try { await scannerRef.current.dispose(); } catch (_) { }
      scannerRef.current = null;
    }
    streamRef.current = null;
  }, []);

  const startScanner = useCallback(async () => {
    if (!scannerContainerRef.current || scannerStartingRef.current) return;
    scannerStartingRef.current = true;
    setCameraError('');

    try {
      const resolveScanText = (result) => {
        if (!result) return '';
        if (typeof result.text === 'string') return result.text.trim();
        if (typeof result.barcodeText === 'string') return result.barcodeText.trim();
        if (typeof result.value === 'string') return result.value.trim();
        return '';
      };

      const scanner = new BarcodeScanner({
        license: "DLS2eyJoYW5kc2hha2VDb2RlIjoiMTA2NDk0NzYxLU1UQTJORGswTnpZeExYZGxZaTFVY21saGJGQnliMm8iLCJtYWluU2VydmVyVVJMIjoiaHR0cHM6Ly9tZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwib3JnYW5pemF0aW9uSUQiOiIxMDY0OTQ3NjEiLCJzdGFuZGJ5U2VydmVyVVJMIjoiaHR0cHM6Ly9zZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwiY2hlY2tDb2RlIjotMjA4NTI2Mjk0Nn0=",
        container: scannerContainerRef.current,
        uiPath: "/dynamsoft/",
        scanMode: EnumScanMode.SM_MULTI_UNIQUE,
        templateFilePath: "/ReadOneDIndustrial.json",
        utilizedTemplateNames: ["ReadOneDIndustrial"],
        duplicateForgetTime: 3000,
        showResultView: false,
        showUploadImageButton: false,
        showPoweredByDynamsoft: false,

        engineResourcePaths: {
          dbrBundle: "/dynamsoft/"
        },

        onUniqueBarcodeScanned: (result) => {
          if (isRecordingRef.current) return;

          const text = resolveScanText(result);
          if (!text) return;

          const now = Date.now();

          if (!(text === lastScanIdRef.current && now - lastScanTimeRef.current < 5000)) {
            lastScanIdRef.current = text;
            lastScanTimeRef.current = now;

            playBeep();
            vibrate();

            setLastScanned(text);
            startRecording(text);
          }
        },

        onInitReady: (components = {}) => {
          const cameraManager = components?.cameraEnhancer?.cameraManager;
          if (cameraManager) {
            cameraManager
              .setMediaStreamConstraints({
                video: {
                  facingMode: { ideal: facingMode },
                  width: { ideal: 640 },
                  height: { ideal: 360 }
                },
                audio: false
              }, true)
              .catch(() => { });
          }
          console.log("Scanner ready");
        },

        onCameraOpen: (components = {}) => {
          const stream = components?.cameraEnhancer?.video?.srcObject;
          if (stream) {
            streamRef.current = stream;
            return;
          }
          const videoEl = scannerContainerRef.current?.querySelector('video');
          if (videoEl?.srcObject) {
            streamRef.current = videoEl.srcObject;
          }
        },

      });

      scannerRef.current = scanner;

      // In SM_MULTI_UNIQUE, launch may not resolve while scanning is active.
      scanner.launch().catch((err) => {
        if (!scannerRef.current?.disposed) {
          setCameraError(err?.message || 'Scanner failed to start');
        }
      });

    } catch (err) {
      setCameraError(err.message || 'Scanner failed to start');
    } finally {
      scannerStartingRef.current = false;
    }
  }, [playBeep, vibrate, startRecording, facingMode]);

  useEffect(() => {
    startScanner();
    return () => { stopScanner(); };
  }, [startScanner, stopScanner]);

  // Load today's recordings on mount
  useEffect(() => {
    api.get('/recordings/my-today').then(({ data }) => {
      const recordings = data.recordings || [];
      setRecentScans(recordings.map(r => ({
        id: r._id || r.id,
        trackingId: r.trackingId,
        startTime: r.startTime,
        status: 'uploaded',
        recordingId: r._id || r.id,
      })));
    }).catch(() => { });
  }, [api]);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => () => {
    try { audioCtxRef.current?.close?.(); } catch (_) { }
  }, []);

  // ─── Camera Flip ─────────────────────────────────────────────────────────────

  const flipCamera = useCallback(() => {
    if (isRecordingRef.current) return;
    stopScanner();
    setFacingMode(f => f === 'environment' ? 'user' : 'environment');
  }, [stopScanner]);

  // ─── UI ──────────────────────────────────────────────────────────────────────

  const statusColors = { scanning: 'bg-green-500', countdown: 'bg-red-500 animate-pulse', uploading: 'bg-yellow-500 animate-pulse' };
  const statusLabels = { scanning: 'Ready to scan', countdown: `Recording — ${countdown}s`, uploading: 'Uploading...' };

  return (
    <div className="fixed inset-0 bg-black flex flex-col overflow-hidden" style={{ touchAction: 'none' }}>

      {/* Camera View */}
      <div className="relative flex-1 overflow-hidden bg-black">

        {/* Dynamsoft BarcodeScanner mounts its <video> inside this div */}
        <div
          ref={scannerContainerRef}
          className="absolute inset-0 w-full h-full"
          style={{ position: 'relative' }}
        />

        {/* Dark overlay with cutout effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 bg-black/50" style={{ height: 'calc(50% - 45px)' }} />
          <div className="absolute bottom-0 left-0 right-0 bg-black/50" style={{ height: 'calc(50% - 45px)' }} />
          <div className="absolute left-0 bg-black/50" style={{ top: 'calc(50% - 45px)', height: '90px', width: '8%' }} />
          <div className="absolute right-0 bg-black/50" style={{ top: 'calc(50% - 45px)', height: '90px', width: '8%' }} />

          {/* Horizontal scan box */}
          <div className="absolute left-[8%] right-[8%]" style={{ top: 'calc(50% - 45px)', height: '90px' }}>
            <div
              className="absolute left-0 right-0 h-0.5 bg-green-400/80"
              style={{ animation: phase === 'scanning' ? 'scanline 2s linear infinite' : 'none', top: '50%' }}
            />
            {['top-0 left-0 border-t-2 border-l-2', 'top-0 right-0 border-t-2 border-r-2',
              'bottom-0 left-0 border-b-2 border-l-2', 'bottom-0 right-0 border-b-2 border-r-2',
            ].map((cls, i) => (
              <div key={i} className={`absolute w-5 h-5 border-green-400 ${cls}`} />
            ))}
            {phase === 'countdown' && countdown !== null && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center">
                  <span className="text-white text-xl font-bold">{countdown}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Camera Error */}
        {cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6 z-10">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-white font-medium mb-1">Camera Error</p>
              <p className="text-zinc-400 text-sm">{cameraError}</p>
              <button onClick={startScanner} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm">
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 pt-4 pb-2 bg-gradient-to-b from-black/60 to-transparent">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-black/40 text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${statusColors[phase]}`} />
              <span className="text-white text-sm font-medium">{statusLabels[phase]}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isOnline ? <Wifi className="w-4 h-4 text-green-400" /> : <WifiOff className="w-4 h-4 text-red-400" />}
            <button
              onClick={flipCamera}
              disabled={phase !== 'scanning'}
              className="p-2 rounded-full bg-black/40 text-white disabled:opacity-40"
            >
              <FlipHorizontal2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scan hint */}
        <div className="absolute bottom-4 left-0 right-0 z-10 text-center pointer-events-none">
          <p className="text-white/70 text-xs">
            {phase === 'scanning' ? 'Align barcode within the box' : ''}
          </p>
        </div>
      </div>

      {/* Bottom Panel */}
      <div className="bg-zinc-950 border-t border-zinc-800 flex flex-col" style={{ maxHeight: '40vh' }}>
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Last Scanned</p>
            <p className="text-white font-mono text-sm mt-0.5 truncate max-w-[220px]">{lastScanned || '—'}</p>
          </div>
          {phase === 'uploading' && (
            <div className="flex items-center gap-2 text-yellow-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs">Uploading</span>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {recentScans.length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-6">No scans yet</p>
          ) : (
            recentScans.map(scan => (
              <div key={scan.id} className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-900">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-mono truncate">{scan.trackingId}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{format(new Date(scan.startTime), 'HH:mm:ss')}</p>
                </div>
                <div className="ml-3 flex-shrink-0 flex items-center gap-2">
                  {scan.status === 'uploading' && <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />}
                  {scan.status === 'uploaded' && (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <button
                        onClick={() => handlePlay(scan)}
                        disabled={loadingPlayId === scan.id}
                        className="p-1.5 rounded-full bg-indigo-600/20 border border-indigo-500/30 text-indigo-400 disabled:opacity-50"
                      >
                        {loadingPlayId === scan.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Play className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => handleDelete(scan)}
                        className="p-1.5 rounded-full bg-red-600/20 border border-red-500/30 text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {scan.status === 'local' && <UploadCloud className="w-4 h-4 text-orange-400" />}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-2 border-t border-zinc-800 flex items-center justify-between">
          <p className="text-zinc-600 text-xs">{user?.name}</p>
          <p className="text-zinc-700 text-xs">Mobile Scanner</p>
        </div>
      </div>

      {/* Video Player Overlay */}
      {playingVideo && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800">
            <button onClick={() => setPlayingVideo(null)} className="p-2 rounded-full bg-zinc-800 text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <p className="text-white text-sm font-mono truncate max-w-[220px]">{playingVideo.trackingId}</p>
            <button onClick={() => setPlayingVideo(null)} className="p-2 rounded-full bg-zinc-800 text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center bg-black">
            <video src={playingVideo.playUrl} controls autoPlay playsInline className="w-full h-full object-contain" />
          </div>
        </div>
      )}

      <style>{`
        @keyframes scanline {
          0%   { transform: translateY(-30px); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(30px); opacity: 0; }
        }
        /* Hide Dynamsoft built-in UI chrome — keep only the video */
        .dce-btn-close,
        .dce-sel-camera,
        .dce-sel-resolution,
        .dce-msg-poweredby { display: none !important; }
        /* Make Dynamsoft video fill our container */
      `}</style>
    </div>
  );
};

export default MobilePackingStation;
